import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Icon from '@material-ui/core/Icon';
import Pagination from '@material-ui/lab/Pagination';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { IconButton } from '@material-ui/core';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import clsx from 'clsx';

import DataService from '../../services/DataService';
import { useEnumContext } from '../../services/EnumService';
import SolutionCard from './SolutionCard';
import styles from './styles';

const stringifySelected = (filterMap) => {
    const selected = Object.keys(filterMap).filter((key) => filterMap[key]);
    return selected.length ? selected.join(',') : null;
};

const Solutions = (props) => {
    const { classes, history } = props;
    const enums = useEnumContext();

    const [sortBy, setSortBy] = useState('created');
    const [totalCount, setTotalCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [search, setSearch] = useState('');
    const [solutions, setSolutions] = useState([]);

    const [equationTypeFilter, setEquationTypeFilter] = useState({ ode: false, pde: false });
    const [problemTypeFilter, setProblemTypeFilter] = useState({ ivp: false, bvp: false });
    const [conditionTypeFilter, setConditionTypeFilter] = useState({ ivp: false, ibvp1d: false, dirichletbvp: false, dirichletbvp2d: false });
    const [equationFilter, setEquationFilter] = useState({});
    const [useCaseFilter, setUseCaseFilter] = useState({});

    const [domainTMin, setDomainTMin] = useState(null);
    const [domainTMax, setDomainTMax] = useState(null);
    const [domainXMin, setDomainXMin] = useState(null);
    const [domainXMax, setDomainXMax] = useState(null);
    const [domainYMin, setDomainYMin] = useState(null);
    const [domainYMax, setDomainYMax] = useState(null);

    const [useCases, setUseCases] = useState([]);
    const [equations, setEquations] = useState([]);
    const [displayEquation, setDisplayEquation] = useState(true);

    const loadSolutions = () => {
        const equationTypes = stringifySelected(equationTypeFilter);
        const problemTypes = stringifySelected(problemTypeFilter);
        const conditionTypes = stringifySelected(conditionTypeFilter);
        const equationIds = stringifySelected(equationFilter);
        const useCaseIds = stringifySelected(useCaseFilter);

        DataService.GetSolutions(
            false,
            sortBy,
            search,
            equationTypes,
            problemTypes,
            conditionTypes,
            equationIds,
            useCaseIds,
            domainTMin,
            domainTMax,
            domainXMin,
            domainXMax,
            domainYMin,
            domainYMax
        ).then((response) => {
            const results = response?.data ?? [];
            setSolutions(results);
            setPageCount(Math.max(1, Math.ceil(results.length / 20)));
        });

        DataService.GetSolutions(
            true,
            null,
            search,
            equationTypes,
            problemTypes,
            conditionTypes,
            equationIds,
            useCaseIds,
            domainTMin,
            domainTMax,
            domainXMin,
            domainXMax,
            domainYMin,
            domainYMax
        ).then((response) => {
            setTotalCount(response?.data?.count ?? 0);
        });
    };

    const loadUseCases = () => {
        DataService.GetUseCases().then((response) => {
            setUseCases(response?.data ?? []);
        });
    };

    const loadEquations = () => {
        DataService.GetEquations().then((response) => {
            setEquations(response?.data ?? []);
        });
    };

    useEffect(() => {
        loadSolutions();
    }, [
        sortBy,
        search,
        equationTypeFilter,
        problemTypeFilter,
        conditionTypeFilter,
        equationFilter,
        useCaseFilter,
        domainTMin,
        domainTMax,
        domainXMin,
        domainXMax,
        domainYMin,
        domainYMax,
    ]);

    useEffect(() => {
        setPageCount(Math.max(1, Math.ceil(totalCount / 20)));
    }, [totalCount]);

    useEffect(() => {
        loadUseCases();
        loadEquations();
    }, []);

    const toggleFilter = (filter, setter, key) => {
        const nextFilter = { ...filter, [key]: !filter[key] };
        setter(nextFilter);
    };

    const handleClickEquationType = (type) => toggleFilter(equationTypeFilter, setEquationTypeFilter, type);
    const handleClickProblemType = (type) => toggleFilter(problemTypeFilter, setProblemTypeFilter, type);
    const handleClickConditionType = (type) => toggleFilter(conditionTypeFilter, setConditionTypeFilter, type);
    const handleClickEquation = (id) => toggleFilter(equationFilter, setEquationFilter, id);
    const handleClickUseCase = (id) => toggleFilter(useCaseFilter, setUseCaseFilter, id);

    const equationTypeColor = (type) => (equationTypeFilter[type] ? 'primary' : 'default');
    const problemTypeColor = (type) => (problemTypeFilter[type] ? 'primary' : 'default');
    const conditionTypeColor = (type) => (conditionTypeFilter[type] ? 'primary' : 'default');
    const equationColor = (id) => (equationFilter[id] ? 'primary' : 'default');
    const ucColor = (id) => (useCaseFilter[id] ? 'primary' : 'default');

    const goToViewSolution = (solution) => {
        history.push(`/solutions/${solution.id}`);
    };

    const plotIconClass = clsx(classes.mediaSwitchIcon, !displayEquation && classes.mediaSwitchIconActive);
    const equationIconClass = clsx(classes.mediaSwitchIcon, displayEquation && classes.mediaSwitchIconActive);
    const plotTextClass = clsx(classes.mediaSwitchText, !displayEquation && classes.mediaSwitchTextActive);
    const equationTextClass = clsx(classes.mediaSwitchText, displayEquation && classes.mediaSwitchTextActive);

    const sortOptions = [
        { value: 'downloads', label: '# Downloads' },
        { value: 'created', label: 'Recently Added' },
        { value: 'updated', label: 'Last Updated' },
        { value: 'alphabetical', label: 'Alphabetical' },
    ];

    const selectedSortLabel = sortOptions.find((option) => option.value === sortBy)?.label || 'Recently Added';

    return (
        <div className={classes.root}>
            <main className={classes.main}>
                <Container maxWidth={false}>
                    <Grid container spacing={3} alignItems="flex-start">
                        <Grid item xs={12} md={3} lg={3}>
                            <Paper className={classes.filterBlock} variant="outlined" square>
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Search
                                    </Typography>
                                    <TextField
                                        placeholder="Search solutions"
                                        type="text"
                                        variant="outlined"
                                        size="small"
                                        onChange={(event) => setSearch(event.target.value)}
                                        value={search}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon color="disabled" fontSize="small">model_training</Icon>
                                                </InputAdornment>
                                            ),
                                            endAdornment:
                                                search && (
                                                    <IconButton aria-label="Clear search" onClick={() => setSearch('')}>
                                                        <CancelRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                ),
                                        }}
                                    />
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Sort By
                                    </Typography>
                                    <TextField
                                        select
                                        variant="outlined"
                                        size="small"
                                        value={sortBy}
                                        onChange={(event) => setSortBy(event.target.value)}
                                    >
                                        {sortOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Display
                                    </Typography>
                                    <div className={classes.mediaSwitch}>
                                        <Icon className={plotIconClass} onClick={() => setDisplayEquation(false)}>insights</Icon>
                                        <Typography className={plotTextClass} component="span" onClick={() => setDisplayEquation(false)}>
                                            Sample Solution
                                        </Typography>
                                        <Icon className={equationIconClass} onClick={() => setDisplayEquation(true)}>functions</Icon>
                                        <Typography className={equationTextClass} component="span" onClick={() => setDisplayEquation(true)}>
                                            Equation
                                        </Typography>
                                    </div>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Equation Type
                                    </Typography>
                                    <div className={classes.filters}>
                                        <Chip
                                            key="filter-ode"
                                            icon={<Icon fontSize="small">show_chart</Icon>}
                                            label="ODE"
                                            clickable
                                            color={equationTypeColor('ode')}
                                            onClick={() => handleClickEquationType('ode')}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            key="filter-pde"
                                            icon={<Icon fontSize="small">legend_toggle</Icon>}
                                            label="PDE"
                                            clickable
                                            color={equationTypeColor('pde')}
                                            onClick={() => handleClickEquationType('pde')}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Problem Type
                                    </Typography>
                                    <div className={classes.filters}>
                                        <Chip
                                            key="filter-problem-ivp"
                                            icon={<Icon fontSize="small">my_location</Icon>}
                                            label="Initial Value Problem"
                                            clickable
                                            color={problemTypeColor('ivp')}
                                            onClick={() => handleClickProblemType('ivp')}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            key="filter-problem-bvp"
                                            icon={<Icon fontSize="small">horizontal_distribute</Icon>}
                                            label="Boundary Value Problem"
                                            clickable
                                            color={problemTypeColor('bvp')}
                                            onClick={() => handleClickProblemType('bvp')}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Condition Type
                                    </Typography>
                                    <div className={classes.filters}>
                                        <Chip
                                            key="filter-condition-ivp"
                                            icon={<Icon fontSize="small">my_location</Icon>}
                                            label={enums?.condition_types?.ivp}
                                            clickable
                                            color={conditionTypeColor('ivp')}
                                            onClick={() => handleClickConditionType('ivp')}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            key="filter-condition-ibvp1d"
                                            icon={<Icon fontSize="small">functions</Icon>}
                                            label={enums?.condition_types?.ibvp1d}
                                            clickable
                                            color={conditionTypeColor('ibvp1d')}
                                            onClick={() => handleClickConditionType('ibvp1d')}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            key="filter-condition-dirichletbvp"
                                            icon={<Icon fontSize="small">backup_table</Icon>}
                                            label={enums?.condition_types?.dirichletbvp}
                                            clickable
                                            color={conditionTypeColor('dirichletbvp')}
                                            onClick={() => handleClickConditionType('dirichletbvp')}
                                            variant="outlined"
                                            size="small"
                                        />
                                        <Chip
                                            key="filter-condition-dirichletbvp2d"
                                            icon={<Icon fontSize="small">apps</Icon>}
                                            label={enums?.condition_types?.dirichletbvp2d}
                                            clickable
                                            color={conditionTypeColor('dirichletbvp2d')}
                                            onClick={() => handleClickConditionType('dirichletbvp2d')}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </div>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Domain
                                    </Typography>
                                    <Box className={classes.domainSection}>
                                        <Typography variant="caption" color="primary" className={classes.domainLabel}>
                                            Time
                                        </Typography>
                                        <Box className={classes.domainInputs}>
                                            <TextField
                                                className={classes.domainInput}
                                                label="min"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainTMin ?? ''}
                                                onChange={(event) => setDomainTMin(event.target.value)}
                                            />
                                            <TextField
                                                className={classes.domainInput}
                                                label="max"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainTMax ?? ''}
                                                onChange={(event) => setDomainTMax(event.target.value)}
                                            />
                                        </Box>
                                    </Box>
                                    <Box className={classes.domainSection}>
                                        <Typography variant="caption" color="primary" className={classes.domainLabel}>
                                            Space
                                        </Typography>
                                        <Box className={classes.domainInputs}>
                                            <TextField
                                                className={classes.domainInput}
                                                label="x min"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainXMin ?? ''}
                                                onChange={(event) => setDomainXMin(event.target.value)}
                                            />
                                            <TextField
                                                className={classes.domainInput}
                                                label="x max"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainXMax ?? ''}
                                                onChange={(event) => setDomainXMax(event.target.value)}
                                            />
                                            <TextField
                                                className={classes.domainInput}
                                                label="y min"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainYMin ?? ''}
                                                onChange={(event) => setDomainYMin(event.target.value)}
                                            />
                                            <TextField
                                                className={classes.domainInput}
                                                label="y max"
                                                variant="outlined"
                                                size="small"
                                                type="number"
                                                value={domainYMax ?? ''}
                                                onChange={(event) => setDomainYMax(event.target.value)}
                                            />
                                        </Box>
                                    </Box>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Differential Equation
                                    </Typography>
                                    <div className={classes.filters}>
                                        {equations.map((eq) => (
                                            <Chip
                                                key={`equation-${eq.id}`}
                                                label={eq.name}
                                                clickable
                                                variant="outlined"
                                                size="small"
                                                color={equationColor(eq.id)}
                                                onClick={() => handleClickEquation(eq.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <Divider light />
                                <div className={classes.filterSection}>
                                    <Typography variant="h6" className={classes.filterHeader}>
                                        Use Case
                                    </Typography>
                                    <div className={classes.filters}>
                                        {useCases.map((uc) => (
                                            <Chip
                                                key={`usecase-${uc.id}`}
                                                label={uc.name}
                                                clickable
                                                variant="outlined"
                                                size="small"
                                                color={ucColor(uc.id)}
                                                onClick={() => handleClickUseCase(uc.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={9} lg={9}>
                            <Box className={classes.resultHeader}>
                                <Typography variant="h5" className={classes.resultTitle}>
                                    Solutions
                                </Typography>
                                <div className={classes.resultMeta}>
                                    <Typography variant="subtitle1" color="textSecondary" className={classes.resultCount}>
                                        {totalCount.toLocaleString()}
                                    </Typography>
                                    <Divider orientation="vertical" flexItem />
                                    <Typography variant="body2" color="textSecondary">
                                        Sorted by {selectedSortLabel}
                                    </Typography>
                                </div>
                            </Box>
                            <Grid container spacing={2} alignItems="stretch">
                                {solutions.map((solution) => (
                                    <Grid item xs={12} sm={6} md={4} key={solution.id || solution.name}>
                                        <SolutionCard
                                            solution={solution}
                                            clickHandle={goToViewSolution}
                                            showStatus
                                            showEquation={displayEquation}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            {totalCount > 0 && (
                                <div className={classes.paginationConatiner}>
                                    <Pagination count={pageCount} variant="outlined" color="primary" />
                                </div>
                            )}
                            {solutions.length === 0 && (
                                <Box component="div" m={3}>
                                    <Typography>No solutions found</Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </div>
    );
};

export default withStyles(styles)(Solutions);
